import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { questions } from "../../data/questions";
import { navigateToTop } from "../../utils/Constants";
import QuestionBank from "../../component/interview-tab/QuestionBank";
import slug from "../../utils/slug";
import useRecordings from "../../hooks/useRecordings";
import "../../styles/pages/interview/_questions-index.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconSearch = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle
            cx="6.5"
            cy="6.5"
            r="4.5"
            stroke="currentColor"
            strokeWidth="1.4"
        />
        <path
            d="M10 10l3.5 3.5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
    </svg>
);

const IconClear = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M3 3l8 8M11 3L3 11"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
    </svg>
);

const IconShuffle = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M11 3h2v2M13 3l-4 4M11 13h2v-2M13 13l-4-4M3 5l2.5 2.5M3 11l4-4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconFilter = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M1 3h13M3.5 7.5h8M6 12h3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
        />
    </svg>
);

const IconCheck = () => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M2 6l3 3 5-5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Mini progress ring ───────────────────────────────────────────────────────
function MiniRing({ pct, size = 36, stroke = 3.5 }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const off = c - (pct / 100) * c;
    return (
        <svg
            width={size}
            height={size}
            aria-hidden="true"
            className="mini-ring"
        >
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                strokeWidth={stroke}
                className="mini-ring__track"
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                strokeWidth={stroke}
                strokeDasharray={c}
                strokeDashoffset={off}
                strokeLinecap="round"
                className="mini-ring__fill"
                style={{
                    transform: "rotate(-90deg)",
                    transformOrigin: "50% 50%",
                    transition: "stroke-dashoffset 0.5s ease",
                }}
            />
        </svg>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function QuestionsIndex() {
    const navigate = useNavigate();
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const [token, setToken] = useState(null);
    const searchRef = useRef(null);

    // Token
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!isAuthenticated) {
                if (!cancelled) setToken(null);
                return;
            }
            try {
                const t = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                    },
                });
                if (!cancelled) setToken(t);
            } catch {
                if (!cancelled) setToken(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, getAccessTokenSilently]);

    // Recordings
    const localRecordings = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("recordings") || "[]");
        } catch {
            return [];
        }
    }, []);

    const { items: serverRecordings } = useRecordings({ token });
    const recordings = useMemo(() => {
        if (token && Array.isArray(serverRecordings) && serverRecordings.length)
            return serverRecordings;
        return localRecordings;
    }, [token, serverRecordings, localRecordings]);

    // Normalise questions
    const items = useMemo(
        () =>
            (questions ?? [])
                .map((q) => {
                    const key = q.slug ?? q.id ?? q.question;
                    if (!key) return null;
                    return { ...q, _key: key };
                })
                .filter(Boolean),
        []
    );

    // Categories
    const categories = useMemo(() => {
        const set = new Set(items.map((q) => q.category || "General"));
        return ["All", ...Array.from(set).sort()];
    }, [items]);

    // Controls
    const [query, setQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [onlyUnseen, setOnlyUnseen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    // Recorded set
    const recordedNames = useMemo(() => {
        const set = new Set();
        recordings.forEach((r) => {
            const key =
                r.question_slug ||
                r.question ||
                r.question_title ||
                r.questionText ||
                r.questionTitle ||
                r.question_id ||
                r.questionId;
            if (key) set.add(slug(key));
        });
        return set;
    }, [recordings]);

    // Filtered list
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter((it) => {
            if (
                selectedCategory !== "All" &&
                (it.category || "General") !== selectedCategory
            )
                return false;
            if (onlyUnseen && recordedNames.has(it.question)) return false;
            if (!q) return true;
            return (
                it.question.toLowerCase().includes(q) ||
                (it.category || "").toLowerCase().includes(q)
            );
        });
    }, [items, query, selectedCategory, onlyUnseen, recordedNames]);

    // Grouped for bank
    const grouped = useMemo(
        () =>
            filtered.reduce((acc, q) => {
                const cat = q.category || "General";
                (acc[cat] ||= []).push(q);
                return acc;
            }, {}),
        [filtered]
    );

    // Stats
    const total = items.length;
    const done = recordedNames.size;
    const pct = total ? Math.round((done / total) * 100) : 0;

    // Active filter count (for badge)
    const activeFilters = [selectedCategory !== "All", onlyUnseen].filter(
        Boolean
    ).length;

    // Handlers
    const handleQuestionClick = useCallback(
        (q) => {
            const key =
                q?._slug ?? q?.slug ?? q?._key ?? q?.id ?? q?.question ?? "";
            const s = slug(key);
            if (!s) return;
            navigate(`/dashboard/questions/${s}`);
        },
        [navigate]
    );

    const randomise = useCallback(() => {
        if (!filtered.length) return;
        const pick = filtered[Math.floor(Math.random() * filtered.length)];
        handleQuestionClick(pick);
        navigateToTop();
    }, [filtered, handleQuestionClick]);

    const clearSearch = () => {
        setQuery("");
        searchRef.current?.focus();
    };
    const resetFilters = () => {
        setSelectedCategory("All");
        setOnlyUnseen(false);
    };

    // Keyboard shortcut: "/" focuses search
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "/" && document.activeElement?.tagName !== "INPUT") {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    return (
        <section className="q-bank">
            {/* ── Page header ── */}
            <div className="q-bank__header">
                <div className="q-bank__heading-row">
                    <div>
                        <p className="q-bank__eyebrow">Practice</p>
                        <h1 className="q-bank__title">Question bank</h1>
                    </div>

                    {/* Progress ring + stats */}
                    <div
                        className="q-bank__progress"
                        aria-label={`${pct}% complete`}
                    >
                        {/* <MiniRing pct={pct} size={48} stroke={4} /> */}
                        <div className="q-bank__progress-text">
                            <span className="q-bank__progress-pct">{pct}%</span>
                            <span className="q-bank__progress-label">
                                {done}/{total} recorded
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Controls row ── */}
                <div className="q-bank__controls">
                    {/* Search */}
                    <div className="q-search" role="search">
                        <span className="q-search__icon">
                            <IconSearch />
                        </span>
                        <input
                            ref={searchRef}
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search questions or categories…"
                            aria-label="Search questions"
                            className="q-search__input"
                            autoComplete="off"
                            spellCheck={false}
                        />
                        {query && (
                            <button
                                className="q-search__clear"
                                onClick={clearSearch}
                                aria-label="Clear search"
                            >
                                <IconClear />
                            </button>
                        )}
                        <span className="q-search__shortcut" aria-hidden="true">
                            /
                        </span>
                    </div>

                    {/* Filter toggle */}
                    <button
                        type="button"
                        className={`q-filter-toggle${filtersOpen ? " is-open" : ""}${activeFilters ? " has-active" : ""}`}
                        onClick={() => setFiltersOpen((v) => !v)}
                        aria-expanded={filtersOpen}
                        aria-controls="q-filter-panel"
                    >
                        <IconFilter />
                        <span>Filters</span>
                        {activeFilters > 0 && (
                            <span
                                className="q-filter-toggle__badge"
                                aria-label={`${activeFilters} active filter${activeFilters > 1 ? "s" : ""}`}
                            >
                                {activeFilters}
                            </span>
                        )}
                    </button>

                    {/* Random question */}
                    <button
                        type="button"
                        className="q-random"
                        onClick={randomise}
                        disabled={filtered.length === 0}
                        aria-label="Practice a random question from the current filter"
                    >
                        <IconShuffle />
                        <span>Random</span>
                    </button>
                </div>

                {/* ── Filter panel ── */}
                {filtersOpen && (
                    <div
                        className="q-filter-panel"
                        id="q-filter-panel"
                        role="group"
                        aria-label="Filter options"
                    >
                        {/* Category chips */}
                        <div className="q-filter-panel__section">
                            <p className="q-filter-panel__label">Category</p>
                            <div
                                className="q-chips"
                                role="listbox"
                                aria-label="Filter by category"
                            >
                                {categories.map((cat) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        role="option"
                                        aria-selected={selectedCategory === cat}
                                        className={`q-chip${selectedCategory === cat ? " is-active" : ""}`}
                                        onClick={() => setSelectedCategory(cat)}
                                    >
                                        {selectedCategory === cat && (
                                            <IconCheck />
                                        )}
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Toggle: only unseen */}
                        <div className="q-filter-panel__section q-filter-panel__section--row">
                            <label className="q-toggle" htmlFor="only-unseen">
                                <input
                                    type="checkbox"
                                    id="only-unseen"
                                    checked={onlyUnseen}
                                    onChange={(e) =>
                                        setOnlyUnseen(e.target.checked)
                                    }
                                    className="q-toggle__input"
                                />
                                <span
                                    className="q-toggle__track"
                                    aria-hidden="true"
                                >
                                    <span className="q-toggle__thumb" />
                                </span>
                                <span className="q-toggle__text">
                                    Show only unrecorded
                                </span>
                            </label>

                            {activeFilters > 0 && (
                                <button
                                    type="button"
                                    className="q-reset"
                                    onClick={resetFilters}
                                >
                                    Reset filters
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Results bar ── */}
                <div
                    className="q-results-bar"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <span className="q-results-bar__count">
                        <strong>{filtered.length}</strong> of {total} questions
                        {selectedCategory !== "All" && (
                            <>
                                {" "}
                                in <em>{selectedCategory}</em>
                            </>
                        )}
                    </span>
                    {query && (
                        <span className="q-results-bar__query">
                            matching "<strong>{query}</strong>"
                        </span>
                    )}
                </div>
            </div>

            {/* ── Question list ── */}
            {filtered.length === 0 ? (
                <div className="q-empty">
                    <div className="q-empty__icon" aria-hidden="true">
                        <IconSearch />
                    </div>
                    <h3 className="q-empty__title">No questions found</h3>
                    <p className="q-empty__body">
                        Try a different search term, select a different
                        category, or turn off the unrecorded filter.
                    </p>
                    <button
                        type="button"
                        className="q-empty__reset"
                        onClick={() => {
                            setQuery("");
                            resetFilters();
                        }}
                    >
                        Clear all filters
                    </button>
                </div>
            ) : (
                <QuestionBank
                    showQuestions
                    groupedQuestions={grouped}
                    handleQuestionClick={handleQuestionClick}
                    questionRef={null}
                    recordings={recordings}
                />
            )}
        </section>
    );
}
